import { navigate } from 'gatsby';
import React from 'react';

import { CurationStatusBadge } from '@components/CurationStatusBadge';
import { TableStatus } from '@components/DataTable';
import type { DataTableRecord } from '@components/DataTable/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Icon from '@components/Icon/Icon';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import { TableContainer } from '@layouts/Layouts/TableLayout';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const ImagingTestListView: React.FC<{ tableData: DataTableRecord[] }> = ({
  tableData,
}) => {
  const {
    state: { isAdmin, isCurator, isPractitioner, isVitall },
  } = useSession();

  return (
    <TableContainer>
      <MuiDataTable
        columns={[
          {
            name: 'viewedStatus',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: boolean) => {
                return <TableStatus value={value} />;
              },
              searchable: false,
              sort: false,
              filter: false,
            },
          },
          {
            name: 'curationStatus',
            label: 'Curation Status',
            className: 'small',
            options: {
              customBodyRender: (status: CurationStatus) => (
                <CurationStatusBadge status={status} />
              ),
              filter: true,
              filterType: 'multiselect',
              display: isAdmin || isCurator,
            },
          },
          {
            name: 'recordSource',
            label: 'Record Source',
            className: 'small',
            options: {
              customBodyRender: (source: ResourceSourceType) => (
                <RecordSourcePill type={source} />
              ),
              filter: isVitall || isPractitioner,
              filterType: 'multiselect',
              display: isVitall || isPractitioner,
            },
          },
          {
            name: 'date',
            className: 'date',
            label: 'Date',
            options: {
              filter: true,
            },
          },
          {
            name: 'testType',
            className: 'small',
            label: 'Test Type',
            options: {
              filter: true,
              searchable: true,
              sort: true,
              filterType: 'multiselect',
            },
          },
          {
            name: 'testName',
            className: 'small',
            label: 'Test Name',
            options: {
              filter: true,
              searchable: true,
              sort: true,
              filterType: 'multiselect',
            },
          },
          {
            name: 'facilityName',
            className: 'small',
            label: 'Facility',
            options: {
              filter: true,
              searchable: true,
              sort: true,
              filterType: 'multiselect',
            },
          },
          {
            name: 'hcpName',
            className: 'small',
            label: 'Practitioner',
            options: {
              filter: true,
              searchable: true,
              sort: true,
              filterType: 'multiselect',
            },
          },
          {
            name: 'hasImages',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: boolean) =>
                value ? <Icon name="images" /> : null,
              searchable: false,
              sort: false,
              filter: false,
            },
          },
          {
            name: 'selfReported',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: boolean) => (
                <SelfReportedPill value={value} />
              ),
              searchable: false,
              sort: false,
              filter: false,
            },
          },
        ]}
        data={tableData}
        options={{
          sortOrder: {
            name: 'date',
            direction: 'desc',
          },
          sort: true,
          search: true,
          filter: true,
          onRowClick: (_rowData, { dataIndex }) => {
            if (dataIndex <= tableData.length - 1) {
              const row = tableData[dataIndex];
              navigate(`${ROUTES.IMAGING_TESTS}${row.id}`);
            }
          },
        }}
        pageParam="imaging"
      />
    </TableContainer>
  );
};

export default ImagingTestListView;
